import { useEffect, useState } from 'react'
import {
  DndContext,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { PickerColumn } from './PickerColumn'
import type { ColumnId, DualListPickerProps, ItemId } from './types'

export function DualListPicker<T>({
  available,
  selected,
  getId,
  renderItem,
  onChange,
  availableLabel = 'Dostępne',
  selectedLabel = 'Aktywne',
  emptyAvailable,
  emptySelected,
}: DualListPickerProps<T>) {
  const [localAvailable, setLocalAvailable] = useState(available)
  const [localSelected, setLocalSelected] = useState(selected)

  useEffect(() => { setLocalAvailable(available) }, [available])
  useEffect(() => { setLocalSelected(selected) }, [selected])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const findColumn = (id: ItemId): ColumnId | null => {
    if (id === 'available' || id === 'selected') return id
    if (localSelected.some(i => getId(i) === id)) return 'selected'
    if (localAvailable.some(i => getId(i) === id)) return 'available'
    return null
  }

  const moveBetween = (activeId: ItemId, fromCol: ColumnId, toCol: ColumnId, overId: ItemId | null) => {
    const fromList = fromCol === 'available' ? localAvailable : localSelected
    const item = fromList.find(i => getId(i) === activeId)
    if (!item) return

    const setFrom = fromCol === 'available' ? setLocalAvailable : setLocalSelected
    const setTo = toCol === 'available' ? setLocalAvailable : setLocalSelected
    const toList = toCol === 'available' ? localAvailable : localSelected

    const insertAt =
      overId && overId !== fromCol && overId !== toCol
        ? toList.findIndex(i => getId(i) === overId)
        : toList.length

    setFrom(prev => prev.filter(i => getId(i) !== activeId))
    setTo(prev => {
      const next = prev.filter(i => getId(i) !== activeId)
      next.splice(insertAt < 0 ? next.length : insertAt, 0, item)
      return next
    })
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return
    const fromCol = findColumn(active.id as ItemId)
    const toCol = findColumn(over.id as ItemId)
    if (!fromCol || !toCol || fromCol === toCol) return
    moveBetween(active.id as ItemId, fromCol, toCol, over.id as ItemId)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) {
      setLocalAvailable(available)
      setLocalSelected(selected)
      return
    }

    const fromCol = findColumn(active.id as ItemId)
    const toCol = findColumn(over.id as ItemId)

    if (fromCol === 'selected' && toCol === 'selected' && active.id !== over.id) {
      const oldIdx = localSelected.findIndex(i => getId(i) === active.id)
      const newIdx = localSelected.findIndex(i => getId(i) === over.id)
      if (oldIdx !== -1 && newIdx !== -1) {
        const reordered = arrayMove(localSelected, oldIdx, newIdx)
        setLocalSelected(reordered)
      }
    }

    queueMicrotask(() => {
      setLocalSelected(currentSelected => {
        const newIds = currentSelected.map(getId)
        const oldIds = selected.map(getId)
        const same =
          newIds.length === oldIds.length && newIds.every((id, i) => id === oldIds[i])
        if (!same) onChange(newIds)
        return currentSelected
      })
    })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="grid grid-cols-2 gap-4">
        <PickerColumn
          id="selected"
          title={selectedLabel}
          items={localSelected}
          getId={getId}
          renderItem={renderItem}
          empty={emptySelected}
        />
        <PickerColumn
          id="available"
          title={availableLabel}
          items={localAvailable}
          getId={getId}
          renderItem={renderItem}
          empty={emptyAvailable}
        />
      </div>
    </DndContext>
  )
}
